<template>
  <div class="map-picker-container">
    <iframe
      ref="mapIframe"
      v-if="visible"
      :id="iframeId"
      :height="height"
      :width="width"
      frameborder="0"
      scrolling="no"
      allow="geolocation"
      :src="mapUrl"
      @load="handleIframeLoad"
    >
    </iframe>
    <div v-if="loading" class="map-loading">
      <i class="el-icon-loading loading-icon" style="font-size: 32px;"></i>
      <span>地图加载中...</span>
    </div>
  </div>
</template>

<script>
export default {
  name: "MapPicker",
  props: {
    visible: {
      type: Boolean,
      default: true
    },
    width: {
      type: [String, Number],
      default: "500px"
    },
    height: {
      type: [String, Number],
      default: "500px"
    },
    iframeId: {
      type: String,
      default: "mapIframe"
    }
  },
  data() {
    return {
      mapUrl: "",
      mapKeys: [],
      mapRetryCount: 0,
      testingKey: false,
      testKeyCallbackId: 0,
      testKeyTimeoutIds: [],
      loading: false
    };
  },
  watch: {
    visible(val) {
      if (val) {
        window.addEventListener("message", this.handleMessage, false);
        if (!this.mapUrl) {
          this.fetchKey();
        }
      } else {
        window.removeEventListener("message", this.handleMessage, false);
      }
    }
  },
  mounted() {
    if (this.visible) {
      window.addEventListener("message", this.handleMessage, false);
      this.fetchKey();
    }
  },
  destroyed() {
    window.removeEventListener("message", this.handleMessage, false);
    this.testKeyTimeoutIds.forEach((id) => {
      clearTimeout(id);
    });
    this.testKeyTimeoutIds = [];
    for (let prop in window) {
      if (prop.startsWith("testMapKey_")) {
        delete window[prop];
      }
    }
  },
  methods: {
    async fetchKey() {
      const { code, result } = await this.$store.dispatch("getParamValueAllowNull", {
        paramCode: "tencent_key"
      });
      if (code === "0" && result.paramValue) {
        this.mapKeys = result.paramValue.split(",");
        await this.findValidKey();
      }
    },
    updateMapUrl(key) {
      if (key) {
        this.mapUrl = `//apis.map.qq.com/tools/locpicker?search=1&policy=1&type=1&key=${key}&referer=myapp`;
      } else if (this.mapKeys.length > 0) {
        const randomKey = this.mapKeys[Math.floor(Math.random() * this.mapKeys.length)];
        this.mapUrl = `//apis.map.qq.com/tools/locpicker?search=1&policy=1&type=1&key=${randomKey}&referer=myapp`;
      }
    },
    findValidKey() {
      if (this.testingKey) {
        return Promise.resolve();
      }
      return new Promise((resolve) => {
        if (this.mapKeys.length === 0) {
          resolve();
          return;
        }
        this.testingKey = true;
        let index = 0;
        const testNextKey = () => {
          if (index >= this.mapKeys.length) {
            this.updateMapUrl();
            this.testingKey = false;
            resolve();
            return;
          }
          const key = this.mapKeys[index];
          this.testKey(key)
            .then((valid) => {
              if (valid) {
                this.updateMapUrl(key);
                this.testingKey = false;
                resolve();
              } else {
                index++;
                testNextKey();
              }
            })
            .catch(() => {
              index++;
              testNextKey();
            });
        };
        testNextKey();
      });
    },
    testKey(key) {
      return new Promise((resolve) => {
        this.testKeyCallbackId++;
        const callbackName = "testMapKey_" + this.testKeyCallbackId;
        const script = document.createElement("script");
        let removed = false;
        const removeScript = () => {
          if (!removed && script.parentNode) {
            removed = true;
            document.head.removeChild(script);
          }
        };
        const timeoutId = setTimeout(() => {
          if (window[callbackName]) {
            delete window[callbackName];
            removeScript();
            const idx = this.testKeyTimeoutIds.indexOf(timeoutId);
            if (idx > -1) {
              this.testKeyTimeoutIds.splice(idx, 1);
            }
            resolve(false);
          }
        }, 3000);
        this.testKeyTimeoutIds.push(timeoutId);
        script.src = `https://h5gw.map.qq.com/ws/place/v1/search?boundary=region(%E5%8C%97%E4%BA%AC%E5%B8%82%2C1)&page_size=1&page_index=1&keyword=%E5%8C%97%E4%BA%AC&key=${key}&apptag=h5pickpoint_place_search&output=jsonp&callback=${callbackName}`;
        script.onerror = () => {
          removeScript();
          const idx = this.testKeyTimeoutIds.indexOf(timeoutId);
          if (idx > -1) {
            this.testKeyTimeoutIds.splice(idx, 1);
          }
          resolve(false);
        };
        script.onload = () => {
          removeScript();
        };
        window[callbackName] = (data) => {
          delete window[callbackName];
          removeScript();
          const idx = this.testKeyTimeoutIds.indexOf(timeoutId);
          if (idx > -1) {
            this.testKeyTimeoutIds.splice(idx, 1);
          }
          resolve(data && data.status === 0);
        };
        document.head.appendChild(script);
      });
    },
    handleMapError() {
      if (this.mapRetryCount < 3) {
        this.mapRetryCount++;
        this.$message.warning(`地图加载失败，正在尝试重新获取密钥...(第${this.mapRetryCount}次)`);
        this.findValidKey();
      } else {
        this.$message.error("地图加载失败，请稍后重试");
        this.mapRetryCount = 0;
      }
    },
    handleIframeLoad() {
      this.loading = false;
    },
    handleMessage(event) {
      const loc = event.data;
      if (loc && loc.module == "locationPicker") {
        this.$emit("locationSelect", {
          poiname: loc.poiname,
          poiaddress: loc.poiaddress,
          longitude: loc.latlng.lng,
          latitude: loc.latlng.lat
        });
      }
    },
    retryLoad() {
      this.mapRetryCount = 0;
      this.testingKey = false;
      this.fetchKey();
    }
  }
};
</script>

<style scoped>
.map-picker-container {
  position: relative;
}
.map-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
}
.loading-icon {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>